/**
 * Воронка выездов /visits-funnel — три колонки канбана:
 *   • Согласование выезда
 *   • Записаны на выезд
 *   • Выезд был
 *
 * Drag-and-drop между колонками: при дропе карточки на другую стадию
 * PATCH /api/visits/{id} {amo_stage: NEW_STAGE}. Backend синхронизирует
 * local status и пушит изменение обратно в Amo через amocrm.set_lead_stage.
 *
 * Источник данных: /api/visits?amo_stage=… (visits-таблица заполняется
 * через POST /api/visits/sync — кнопка «Подтянуть из AmoCRM»).
 */
import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import {
  fetchVisitsList, updateVisit, syncVisitsFromAmo, addDealReportNote, ensureVisitProductionCard,
  type VisitNote,
  type Visit,
} from "@/api/dashboard";
import { parseBackendIso } from "@/lib/presence";


const STAGES: { key: string; label: string; accent: string }[] = [
  { key: "Согласование выезда", label: "Согласование выезда", accent: "#3390ec" },
  { key: "Записаны на выезд",   label: "Записаны на выезд",   accent: "#a85f12" },
  { key: "Выезд был",           label: "Выезд был",           accent: "#4fae4e" },
];

const DRAG_MIME = "application/x-kb-visit";
type DragPayload = { id: number; fromStage: string };

function parseDragPayload(raw: string): DragPayload | null {
  try {
    const v = JSON.parse(raw);
    if (typeof v?.id === "number" && typeof v?.fromStage === "string") return v;
  } catch { /* ignore */ }
  return null;
}


export function VisitsFunnelPage() {
  const qc = useQueryClient();
  // exclude_post_contract: сделки уже прошедшие «Договор заключён» / «Договор
  // отложенный» / в Сопровождении НЕ должны торчать в воронке Выезды. Икша,
  // например, у которого в Amo «Договор заключён», иначе оставался бы в этой
  // воронке из-за устаревшего amo_stage в visits-таблице.
  const q1 = useQuery({
    queryKey: ["visits-funnel", "Согласование выезда"],
    queryFn: () => fetchVisitsList({ amo_stage: "Согласование выезда", exclude_post_contract: true }),
  });
  const q2 = useQuery({
    queryKey: ["visits-funnel", "Записаны на выезд"],
    queryFn: () => fetchVisitsList({ amo_stage: "Записаны на выезд", exclude_post_contract: true }),
  });
  const q3 = useQuery({
    queryKey: ["visits-funnel", "Выезд был"],
    queryFn: () => fetchVisitsList({ amo_stage: "Выезд был", exclude_post_contract: true }),
  });

  const syncMut = useMutation({
    mutationFn: syncVisitsFromAmo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visits-funnel"] });
    },
  });

  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // DnD: при дропе на новую колонку — PATCH amo_stage
  const moveMut = useMutation({
    mutationFn: ({ id, amo_stage }: { id: number; amo_stage: string }) =>
      updateVisit(id, { amo_stage }),
    onMutate: async ({ id, amo_stage }) => {
      // Оптимистично — перемещаем карточку в UI сразу
      await qc.cancelQueries({ queryKey: ["visits-funnel"] });
      const prevs: Record<string, unknown> = {};
      for (const stage of STAGES.map(s => s.key)) {
        const key = ["visits-funnel", stage];
        prevs[stage] = qc.getQueryData(key);
        qc.setQueryData(key, (old: { items?: Visit[] } | undefined) => {
          if (!old?.items) return old;
          return { ...old, items: old.items.filter(v => v.id !== id) };
        });
      }
      // Добавляем карточку в target колонку (берём из prev-state)
      for (const stage of STAGES.map(s => s.key)) {
        const items = (prevs[stage] as { items?: Visit[] } | undefined)?.items;
        const found = items?.find(v => v.id === id);
        if (found) {
          qc.setQueryData(["visits-funnel", amo_stage], (old: { items?: Visit[] } | undefined) => {
            if (!old) return { items: [{ ...found, amo_stage }], kpi: {} };
            return { ...old, items: [{ ...found, amo_stage }, ...(old.items ?? [])] };
          });
          break;
        }
      }
      return { prevs };
    },
    onError: (_e, _vars, ctx) => {
      // Откат
      if (ctx?.prevs) {
        for (const stage of STAGES.map(s => s.key)) {
          qc.setQueryData(["visits-funnel", stage], ctx.prevs[stage]);
        }
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["visits-funnel"] }),
  });

  function handleDrop(e: React.DragEvent<HTMLDivElement>, targetStage: string) {
    e.preventDefault();
    setDragOverKey(null);
    const raw = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData("text/plain");
    const data = parseDragPayload(raw);
    if (!data) return;
    if (data.fromStage === targetStage) return;
    moveMut.mutate({ id: data.id, amo_stage: targetStage });
  }

  if (q1.isLoading || q2.isLoading || q3.isLoading) return <Spinner size="lg" label="Загрузка…"/>;

  const cols: Record<string, Visit[]> = {
    "Согласование выезда": q1.data?.items ?? [],
    "Записаны на выезд":   q2.data?.items ?? [],
    "Выезд был":           q3.data?.items ?? [],
  };

  return (
    <div className="page visits-funnel-page" style={{ padding: 16, display: "flex", flexDirection: "column", height: "calc(100vh - 80px)" }}>
      <div className="visits-funnel-head">
        <h1 className="visits-funnel-title">Воронка выездов</h1>
        <span className="visits-funnel-sub">
          Сделки в стадиях «Согласование выезда», «Записаны на выезд» и «Выезд был»
        </span>
        <button
          className="btn btn-sm visits-funnel-sync"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
        >
          <Icon name="refresh" size={11}/>{" "}
          {syncMut.isPending ? "Синхронизация…" : "Подтянуть из AmoCRM"}
        </button>
      </div>

      <div style={{
        flex: 1, minHeight: 0,
        display: "grid",
        gridTemplateColumns: `repeat(${STAGES.length}, minmax(320px, 1fr))`,
        gap: 12,
        overflowX: "auto",
        overflowY: "hidden",
      }}>
        {STAGES.map(s => (
          <FunnelColumn
            key={s.key}
            stage={s.key}
            title={s.label}
            accent={s.accent}
            items={cols[s.key] ?? []}
            isDragOver={dragOverKey === s.key}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setDragOverKey(s.key);
            }}
            onDragLeave={() => setDragOverKey(prev => prev === s.key ? null : prev)}
            onDrop={(e) => handleDrop(e, s.key)}
          />
        ))}
      </div>
    </div>
  );
}


function FunnelColumn({
  stage, title, accent, items, isDragOver,
  onDragOver, onDragLeave, onDrop,
}: {
  stage: string;
  title: string;
  accent: string;
  items: Visit[];
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: "flex", flexDirection: "column", minHeight: 0,
        background: isDragOver ? "#f0f7f0" : "#fafafa",
        border: `1px solid ${isDragOver ? accent : "#ececec"}`,
        borderRadius: 14,
        overflow: "hidden",
        transition: "background-color 100ms, border-color 100ms",
    }}>
      <div style={{
        padding: "12px 14px",
        borderBottom: `2px solid ${accent}`,
        background: "#ffffff",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>
            {title}
          </h3>
          <span style={{
            background: accent, color: "#fff",
            padding: "2px 8px", borderRadius: 10,
            fontSize: 11, fontWeight: 600,
          }}>
            {items.length}
          </span>
        </div>
      </div>
      <div style={{
        flex: 1, minHeight: 0, overflowY: "auto",
        padding: 10,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        {items.length === 0 && (
          <div style={{ color: "var(--mute)", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
            Сделок нет
          </div>
        )}
        {items.map(v => <VisitCard key={v.id} v={v} stage={stage}/>)}
      </div>
    </div>
  );
}


function VisitCard({ v, stage }: { v: Visit; stage: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState("");
  const [localNotes, setLocalNotes] = useState<VisitNote[]>(v.notes ?? []);

  const markCompleted = useMutation({
    mutationFn: () => updateVisit(v.id, { status: "completed" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["visits-funnel"] }),
  });

  const saveNote = useMutation({
    mutationFn: () => addDealReportNote(v.amo_lead_id!, note, true, "visits"),
    onSuccess: (res) => {
      setLocalNotes(prev => [{
        id: res.note.id,
        text: res.note.text,
        author_name: res.note.author_name,
        created_at: res.note.created_at,
      }, ...prev]);
      setNote("");
      if (!res.amo_note_id) {
        alert("В локальную базу записал, но в AmoCRM не получилось — проверь токен.");
      }
    },
    onError: (e) => alert(`Ошибка: ${e instanceof Error ? e.message : String(e)}`),
  });

  const visitAt = v.visit_at ? parseBackendIso(v.visit_at) ?? new Date(v.visit_at) : null;
  const canMarkCompleted = v.status !== "completed" && v.amo_stage !== "Выезд был";

  function onDragStart(e: React.DragEvent<HTMLDivElement>) {
    const payload = JSON.stringify({ id: v.id, fromStage: stage });
    e.dataTransfer.setData(DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  }

  const [opening, setOpening] = useState(false);
  async function openProductionCard(e: React.MouseEvent) {
    // Не открываем если кликнули по интерактивным элементам
    const t = e.target as HTMLElement;
    if (t.closest("button, a, textarea, input")) return;

    let projectId = v.production_project_id;
    if (!projectId) {
      if (!v.amo_lead_id) {
        if (v.client_amo_url) window.open(v.client_amo_url, "_blank", "noopener,noreferrer");
        return;
      }
      try {
        setOpening(true);
        const res = await ensureVisitProductionCard(v.id);
        projectId = res.project_id;
      } catch (err) {
        alert(`Не получилось открыть карточку производства: ${err instanceof Error ? err.message : String(err)}`);
        setOpening(false);
        return;
      } finally {
        setOpening(false);
      }
    }
    void navigate({ to: "/production/$projectId", params: { projectId: String(projectId) } });
  }

  const handleSubmitNote = () => {
    if (!note.trim() || saveNote.isPending) return;
    if (!v.amo_lead_id) {
      alert("У сделки нет amo_lead_id — примечание добавить нельзя.");
      return;
    }
    saveNote.mutate();
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={() => setDragging(false)}
      onClick={openProductionCard}
      style={{
        background: "#ffffff",
        border: "1px solid #e6e6e6",
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        transition: "border-color 120ms, box-shadow 120ms, transform 120ms, opacity 120ms",
        // minHeight убран — карточка теперь без region/manager/engineer,
        // высоту должно определять содержимое (title + date + composer).
        boxSizing: "border-box",
        cursor: opening ? "wait" : (v.amo_lead_id || v.client_amo_url) ? "pointer" : "grab",
        opacity: dragging ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#4fae4e";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e6e6e6";
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Заголовок */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, justifyContent: "space-between" }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", minHeight: 35, flex: 1,
        }}>
          {v.client_name}
        </div>
        {v.client_amo_url && (
          <a href={v.client_amo_url} target="_blank" rel="noopener noreferrer"
             title="Открыть в AmoCRM" onClick={(e) => e.stopPropagation()}
             style={{ color: "var(--mute)", flexShrink: 0, lineHeight: 1, padding: 2 }}>
            <Icon name="external" size={12}/>
          </a>
        )}
      </div>

      {/* region / manager_name / engineer_name — скрыты в карточке-воронке
          по запросу пользователя (мелко, не читается). Все эти поля
          доступны в карточке производства, куда ведёт клик. */}

      {/* Дата визита */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        fontSize: 13, paddingTop: 4, borderTop: "1px dotted var(--line)",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Дата выезда</div>
          <div style={{ fontWeight: 600, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
            {visitAt
              ? visitAt.toLocaleString("ru", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
              : "—"}
          </div>
        </div>
        {v.amo_stage && (
          <span style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 10,
            background: "var(--paper-2)", color: "var(--ink-3)",
            border: "1px solid var(--line)",
          }}>{v.amo_stage}</span>
        )}
      </div>

      {/* Примечания (если есть) */}
      {localNotes.length > 0 && (
        <div style={{
          background: "linear-gradient(180deg, #fffbeb 0%, #fff8e1 100%)",
          border: "1px solid #ffd54f",
          borderRadius: 8,
          padding: "6px 8px",
          fontSize: 11.5,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: "#7c5300",
            marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4,
          }}>
            💬 {localNotes.length}
          </div>
          {localNotes.slice(0, 3).map((n, i) => (
            <div key={n.id} style={{
              marginBottom: i === Math.min(2, localNotes.length - 1) ? 0 : 4,
            }}>
              <div style={{ fontSize: 10, color: "rgba(124,83,0,0.7)" }}>
                {n.author_name} · {fmtNoteDate(n.created_at)}
              </div>
              <div style={{ color: "#3a2700", whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{n.text}</div>
            </div>
          ))}
          {localNotes.length > 3 && (
            <div style={{ fontSize: 10, color: "rgba(124,83,0,0.6)", marginTop: 4 }}>
              + ещё {localNotes.length - 3}…
            </div>
          )}
        </div>
      )}

      {/* Inline композер примечания + кнопка «Состоялся» в одной строке */}
      {v.amo_lead_id && (
        <div style={{ display: "flex", gap: 4, alignItems: "stretch" }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Примечание (Amo + Производство)…"
            rows={1}
            style={{
              flex: 1, resize: "none", minHeight: 30, fontSize: 11.5,
              padding: "4px 8px",
              border: "1px solid var(--line)",
              borderRadius: 6, background: "#fafafa",
              fontFamily: "inherit",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSubmitNote();
              }
            }}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleSubmitNote(); }}
            disabled={!note.trim() || saveNote.isPending}
            style={{
              padding: "0 10px", fontSize: 11, fontWeight: 600,
              background: note.trim() && !saveNote.isPending ? "#4fae4e" : "#e0e0e0",
              color: note.trim() && !saveNote.isPending ? "#fff" : "var(--mute)",
              border: "none", borderRadius: 6,
              cursor: note.trim() && !saveNote.isPending ? "pointer" : "default",
              flexShrink: 0,
            }}
          >{saveNote.isPending ? "…" : "✓"}</button>
          {canMarkCompleted && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); markCompleted.mutate(); }}
              disabled={markCompleted.isPending}
              title="Состоялся → переведём в «Выезд был» в Amo"
              style={{
                padding: "0 10px", fontSize: 11, fontWeight: 600,
                background: markCompleted.isPending ? "#9ec59d" : "#4fae4e",
                color: "#fff",
                border: "none", borderRadius: 6,
                cursor: markCompleted.isPending ? "wait" : "pointer",
                flexShrink: 0, whiteSpace: "nowrap",
              }}
            >
              {markCompleted.isPending ? "…" : "Состоялся"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function fmtNoteDate(iso: string): string {
  try {
    const d = new Date(iso.replace(" ", "T") + "Z");
    return d.toLocaleString("ru", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16);
  }
}
